import styled, { css, DefaultTheme } from 'styled-components';
import { hexToRGB } from '@app/utils/utils';

/**
 * Pieces specific to the orders screen. The table chrome itself now lives in
 * components/common/Table so every admin table shares it.
 */

export const TONE = {
  serial: '#62d7c6',
  email: '#34AAD0',
  manual: '#ffaa00',
  unmatched: '#9A9B9F',
} as const;

// Derived from the theme's ink colour so these hold in light mode too --
// hard-coded white was invisible against a light background.
const hairline = (p: { theme: DefaultTheme }) => hexToRGB(p.theme.colors.text.main, 0.07);
const muted = (p: { theme: DefaultTheme }) => hexToRGB(p.theme.colors.text.main, 0.45);

/** Numerals line up column-to-column; without this, dates and IDs jitter. */
const tabular = css`
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
`;

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Tile = styled.button<{ $tone: string; $active: boolean }>`
  appearance: none;
  text-align: left;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${(p) => hexToRGB(p.theme.colors.text.main, p.$active ? 0.08 : 0.03)};
  border: 1px solid ${(p) => (p.$active ? p.$tone : hairline(p))};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--soft-panel-hover);
    border-color: ${({ $tone }) => $tone};
  }

  &:focus-visible {
    outline: 2px solid ${({ $tone }) => $tone};
    outline-offset: 2px;
  }
`;

export const TileValue = styled.div<{ $tone: string }>`
  ${tabular};
  font-size: 22px;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${({ $tone }) => $tone};
`;

export const TileLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  font-size: 14px;
  font-weight: 500;
  color: ${muted};
`;

/** Status pill. Replaces AntD's Tag, which is taller and has a hard border. */
export const Pill = styled.span<{ $tone: string }>`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $tone }) => $tone};
  background: ${({ $tone }) => `${$tone}1f`};
`;

export const Serial = styled.span`
  ${tabular};
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  padding: 3px 7px;
  border-radius: 6px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 14px;
  background: var(--soft-panel-hover);
`;

/** Primary line of a two-line cell (product title, user name). */
export const CellMain = styled.div`
  font-size: 14px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Secondary line: brand · SKU, or the user's email. */
export const CellSub = styled.div`
  font-size: 14px;
  line-height: 1.35;
  margin-top: 1px;
  color: ${muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Muted = styled.span`
  color: ${muted};
`;

export const DateCell = styled.span`
  ${tabular};
  font-size: 14px;
  white-space: nowrap;
  color: ${(p) => hexToRGB(p.theme.colors.text.main, 0.75)};
`;

export const OrderCell = styled.span`
  ${tabular};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;
